import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_premium-brew-36/artifacts/glj0sgxs_WhatsApp%20Image%202026-02-18%20at%2018.37.17.jpeg";

const navLinks = [
  { name: 'Ana Sayfa', href: '#hero' },
  { name: 'Hakkımızda', href: '#about' },
  { name: 'Menü', href: '/menu', isPage: true },
  { name: 'Yorumlar', href: '#reviews' },
  { name: 'İletişim', href: '#contact' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'navbar-blur py-2' : 'py-4 bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection('#hero');
            }}
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Polje Coffee Co" 
              className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <span className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold text-[#f5f0e1]">
              Polje Coffee Co
            </span>
          </a>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <li key={link.name}>
                {link.isPage ? (
                  <a
                    href={link.href}
                    className="text-[#b8c4b8] hover:text-[#d4a84b] transition-colors duration-300 text-sm font-medium tracking-wide"
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-[#b8c4b8] hover:text-[#d4a84b] transition-colors duration-300 text-sm font-medium tracking-wide"
                    data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </a>
                )}
              </li>
            ))}
          </ul>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-[#f5f0e1] hover:text-[#d4a84b] transition-colors"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#1a2e23] pt-24 md:hidden"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col items-center gap-8 p-8">
              {navLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.isPage ? (
                    <a
                      href={link.href}
                      className="text-2xl font-['Playfair_Display'] text-[#f5f0e1] hover:text-[#d4a84b] transition-colors"
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      className="text-2xl font-['Playfair_Display'] text-[#f5f0e1] hover:text-[#d4a84b] transition-colors"
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </a>
                  )}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
